import Modal from "@/Components/Modal";
import SecondaryButton from "@/Components/SecondaryButton";
import DangerButton from "@/Components/DangerButton";
import {getLanguageName} from "@/utils/textFormatting";

export type DeleteOption = 'current-language' | 'all';

export type DeleteConfirmationModalProps = {
    show: boolean;
    onClose: () => void;
    onConfirm: (option: DeleteOption) => void;
    currentLanguage: string;
    availableLanguages: string[];
}

export default function DeleteConfirmationModal({
    show,
    onClose,
    onConfirm,
    currentLanguage,
    availableLanguages
}: DeleteConfirmationModalProps) {
    const hasMultipleLanguages = availableLanguages.length > 1;
    const isOriginalLanguage = currentLanguage === availableLanguages[0];
    const canDeleteCurrentLanguage = hasMultipleLanguages && !isOriginalLanguage;

    const handleDelete = (option: DeleteOption) => {
        onConfirm(option);
        onClose();
    };

    return (
        <Modal maxWidth='md' show={show} onClose={onClose}>
            <div className="p-6">
                <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Delete Subtitles
                </h2>

                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {canDeleteCurrentLanguage
                        ? `${availableLanguages.length} languages available. Removed subtitles will be permanently deleted.`
                        : hasMultipleLanguages
                        ? `Deleting will permanently remove the subtitles in all ${availableLanguages.length} languages. This cannot be undone.`
                        : 'This action cannot be undone. All subtitle data will be permanently deleted.'
                    }
                </p>

                <div className="mt-6 flex justify-end gap-1 flex-nowrap">
                    <SecondaryButton className="me-2" onClick={onClose}>
                        Cancel
                    </SecondaryButton>

                    {canDeleteCurrentLanguage ? (
                        <>
                            <SecondaryButton
                                onClick={() => handleDelete('current-language')}
                                className="truncate shrink me-2"
                            >
                                Delete {getLanguageName(currentLanguage)}
                            </SecondaryButton>

                            <DangerButton
                                onClick={() => handleDelete('all')}
                                className="shrink whitespace-nowrap"
                            >
                                Delete All
                            </DangerButton>
                        </>
                    ) : (
                        <DangerButton onClick={() => handleDelete('all')}>
                            Delete
                        </DangerButton>
                    )}
                </div>
            </div>
        </Modal>
    );
}
